import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  Stepper,
  StepSeparator,
  StepStatus,
  StepTitle,
  Text
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { ImportSources, ImportStatuses } from '../../../constants/importStatuses';
import { useImportFiles } from '../../../services/import-service';

import ImportActions from './ImportActions';
import ImportManifestTools from './ImportManifestTools';
import ImportPreview from './ImportPreview';
import ImportRowsTable from './ImportRowsTable';
import { DropboxPanel, DryRunPanel, RollbackPanel, RunPanel } from './ImportSummary';
import ImportUploads from './ImportUploads';
import { defaultStep, furthestStep, isDropboxBusy, needsRevalidation, WizardSteps } from './util';

const STEPS = [
  { step: WizardSteps.files, title: 'Files', description: 'Upload or pull' },
  { step: WizardSteps.manifest, title: 'Manifest', description: 'Draft and validate' },
  { step: WizardSteps.review, title: 'Review', description: 'Dry run and blanks' },
  { step: WizardSteps.preview, title: 'Preview', description: 'Confirm and run' },
  { step: WizardSteps.run, title: 'Run', description: 'Progress' }
];

const NEXT_HINTS = {
  [WizardSteps.files]: 'Stage some files to continue',
  [WizardSteps.manifest]: 'Validate the manifest to continue',
  [WizardSteps.review]: 'Fix the errors and validate again to continue'
};

const FilesStep = ({ importData }) => (
  <Box>
    {importData.summary?.dropbox && (isDropboxBusy(importData) || ['failed', 'done', 'cancelled'].includes(importData.summary.dropbox.status)) && (
      <Box mb={4}><DropboxPanel dropbox={importData.summary.dropbox} /></Box>
    )}
    {importData.source === ImportSources.dropbox && (
      <Box mb={4}><ImportActions importData={importData} only={['stageFromDropbox', 'cancelPull']} /></Box>
    )}
    <ImportUploads importData={importData} showManifest={false} />
  </Box>
);

FilesStep.propTypes = { importData: PropTypes.object.isRequired };

const ManifestStep = ({ importData }) => (
  <Box>
    <Text color="greyScale.500" mb={3} textStyle="bodySm">
      Draft a manifest from the staged files, or upload your own, then validate it. The dry run checks every row without creating anything.
    </Text>
    <Box mb={4}><ImportManifestTools importData={importData} tools={['draft', 'download']} /></Box>
    <Box mb={4}><ImportUploads importData={importData} showFiles={false} /></Box>
    <ImportActions importData={importData} only={['validate']} />
  </Box>
);

ManifestStep.propTypes = { importData: PropTypes.object.isRequired };

const ReviewStep = ({ importData }) => (
  <Box>
    {importData.summary?.rows && <Box mb={4}><DryRunPanel summary={importData.summary} /></Box>}
    <Flex alignItems="flex-start" flexWrap="wrap" gap={3} justifyContent="space-between" mb={4}>
      <ImportManifestTools importData={importData} tools={['fill', 'download']} />
      <ImportActions importData={importData} only={['validate', 'report']} />
    </Flex>
    <ImportRowsTable importData={importData} />
  </Box>
);

ReviewStep.propTypes = { importData: PropTypes.object.isRequired };

const PreviewStep = ({ importData }) => (
  <Box>
    <Text color="greyScale.500" mb={3} textStyle="bodySm">
      This is what the run will create. Go back to change a row, or run the import.
    </Text>
    <ImportPreview importData={importData} />
  </Box>
);

PreviewStep.propTypes = { importData: PropTypes.object.isRequired };

const RunStep = ({ importData }) => (
  <Box>
    {importData.summary?.run && <Box mb={4}><RunPanel importData={importData} /></Box>}
    {importData.summary?.rollback && importData.status === ImportStatuses.rolledBack && <Box mb={4}><RollbackPanel rollback={importData.summary.rollback} /></Box>}
    <ImportActions importData={importData} only={['pause', 'resume', 'rollback', 'report']} />
  </Box>
);

RunStep.propTypes = { importData: PropTypes.object.isRequired };

const STEP_BODIES = {
  [WizardSteps.files]: FilesStep,
  [WizardSteps.manifest]: ManifestStep,
  [WizardSteps.review]: ReviewStep,
  [WizardSteps.preview]: PreviewStep,
  [WizardSteps.run]: RunStep
};

/**
 * The import as five steps. The import's state decides how far the steps are
 * unlocked and where the wizard lands when that changes; earlier steps stay
 * open so files and rows can be changed before the run.
 */
const ImportWizard = ({ importData }) => {
  const { data: files } = useImportFiles(importData.id);
  const stagedTotal = files?.total ?? 0;

  const furthest = furthestStep(importData, stagedTotal);
  const landing = defaultStep(importData, stagedTotal);
  const [step, setStep] = useState(landing);

  useEffect(() => {
    setStep(landing);
  }, [landing]);

  const current = Math.min(step, furthest);
  const Body = STEP_BODIES[current];
  const canGoNext = current < furthest;

  return (
    <Box>
      <Stepper index={current - 1} mb={4} size="sm">
        {STEPS.map(entry => (
          <Step
            cursor={entry.step <= furthest ? 'pointer' : 'not-allowed'}
            data-testid={`wizard-step-${entry.step}`}
            key={entry.step}
            opacity={entry.step <= furthest ? 1 : 0.5}
            onClick={() => entry.step <= furthest && setStep(entry.step)}
          >
            <StepIndicator>
              <StepStatus active={<StepNumber />} complete={<StepIcon />} incomplete={<StepNumber />} />
            </StepIndicator>
            <Box flexShrink="0">
              <StepTitle>{entry.title}</StepTitle>
              <StepDescription>{entry.description}</StepDescription>
            </Box>
            <StepSeparator />
          </Step>
        ))}
      </Stepper>

      <Flex alignItems="center" justifyContent="space-between" mb={6}>
        <Button data-testid="wizard-back" isDisabled={current <= WizardSteps.files} variant="secondary" onClick={() => setStep(current - 1)}>
          Back
        </Button>
        <Flex alignItems="center" gap={3}>
          {!canGoNext && current < WizardSteps.preview && <Text color="greyScale.500" textStyle="bodySm">{NEXT_HINTS[current]}</Text>}
          {current < WizardSteps.preview && (
            <Button data-testid="wizard-next" isDisabled={!canGoNext} variant="primary" onClick={() => setStep(current + 1)}>
              {`Next: ${STEPS[current].title}`}
            </Button>
          )}
          {current === WizardSteps.preview && <ImportActions importData={importData} only={['run']} />}
        </Flex>
      </Flex>

      {importData.error && (
        <Alert borderRadius="md" mb={4} status="error">
          <AlertIcon />
          <AlertDescription>{importData.error}</AlertDescription>
        </Alert>
      )}

      {needsRevalidation(importData) && current >= WizardSteps.review && (
        <Alert borderRadius="md" mb={4} status="warning">
          <AlertIcon />
          <AlertDescription>Rows or files changed since the last dry run; validate again before running.</AlertDescription>
        </Alert>
      )}

      <Box data-testid={`wizard-body-${current}`}>
        <Body importData={importData} />
      </Box>
    </Box>
  );
};

ImportWizard.propTypes = { importData: PropTypes.object.isRequired };

export default ImportWizard;
